// Broadcasting Center v2 (PRD §4.6 / WhatChimp §1.5 / WP-02).
// Full parity implementation with live stats, multi-account & status filtering,
// actionable campaign controls (View, Pause/Resume, Cancel, Export, Delete),
// and zero mock dependencies.

import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../lib/i18n';
import { ALL_ACCOUNTS, numberLabel, useAccounts } from '../lib/accounts';
import {
  whatsappListBroadcasts,
  whatsappBroadcastStats,
  whatsappPauseBroadcast,
  whatsappResumeBroadcast,
  whatsappCancelBroadcast,
  whatsappDeleteBroadcast,
  whatsappExportBroadcastReport,
  type Campaign,
  type BcastStatus,
  type BroadcastStats,
  type BroadcastDoc,
  statusToAllowedActions,
  mapBroadcast,
} from '../lib/broadcasts';
import { PageHeader } from '../components/ui/PageHeader';
import { PillTabs } from '../components/ui/PillTabs';
import { FilterBar } from '../components/ui/FilterBar';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { useToast } from '../components/ui/Toast';
import { useConfirm } from '../components/ui/ConfirmDialog';
import MetricBar from '../components/ui/MetricBar';
import {
  BroadcastStatCards,
  CreateCampaignDrawer,
  BroadcastDetailDrawer,
} from '../components/broadcast';
import {
  PlusIcon,
  EyeIcon,
  PlayIcon,
  PauseIcon,
  StopCircleIcon,
  DownloadIcon,
  TrashIcon,
  SmartphoneIcon,
  RefreshIcon,
  BroadcastingIcon,
} from '../components/icons';

const METRIC_COLS = [
  { field: 'processed', labelKey: 'dash.col.processed', icon: '✈️', bar: 'bg-primary' },
  { field: 'delivered', labelKey: 'dash.col.delivered', icon: '✅', bar: 'bg-accent' },
  { field: 'opened', labelKey: 'dash.col.opened', icon: '👁️', bar: 'bg-amber-500' },
  { field: 'unreached', labelKey: 'dash.col.unreached', icon: '⛔', bar: 'bg-ink-3' },
] as const;

const STATUS_BADGE: Record<
  BcastStatus,
  { variant: 'slate' | 'warning' | 'info' | 'success' | 'danger'; dot: boolean }
> = {
  draft: { variant: 'slate', dot: true },
  scheduled: { variant: 'warning', dot: true },
  sending: { variant: 'info', dot: true },
  completed: { variant: 'success', dot: true },
  paused: { variant: 'warning', dot: true },
  cancelled: { variant: 'danger', dot: true },
  failed: { variant: 'danger', dot: true },
};

export default function Broadcasting() {
  const { t } = useI18n();
  const toast = useToast();
  const confirm = useConfirm();
  const accounts = useAccounts();

  // Channel tab (All vs WhatsApp vs SMS)
  const [activeChannel, setActiveChannel] = useState<'all' | 'whatsapp' | 'sms'>('all');

  // Stats & Campaigns state
  const [stats, setStats] = useState<BroadcastStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);

  // Filters state
  const [selectedAccountId, setSelectedAccountId] = useState<string>(accounts.selectedId);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // Keep local filter in sync with global account switcher if changed externally
  useEffect(() => {
    setSelectedAccountId(accounts.selectedId);
  }, [accounts.selectedId]);

  // Drawer states
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [selectedDetailId, setSelectedDetailId] = useState<string | null>(null);
  const [busyCampaignId, setBusyCampaignId] = useState<string | null>(null);

  // Load 30-day stats
  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await whatsappBroadcastStats({
        days: 30,
        channel: activeChannel !== 'all' ? activeChannel : undefined,
      });
      if (res.ok) {
        setStats({
          totalCampaigns: res.totalCampaigns,
          completed: res.completed,
          avgOpenRate: res.avgOpenRate,
          totalSent: res.totalSent,
          failedUnreachedRate: res.failedUnreachedRate,
        });
      }
    } catch {
      // Quiet fail or fallback
    } finally {
      setLoadingStats(false);
    }
  }, [activeChannel]);

  // Debounce the search box: keyed directly on `searchQuery`, every keystroke
  // fired a whatsappListBroadcasts callable.
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  useEffect(() => {
    const h = window.setTimeout(() => setDebouncedSearch(searchQuery), 350);
    return () => window.clearTimeout(h);
  }, [searchQuery]);

  // Load campaign list. `toast` is intentionally NOT a dependency: it is now
  // referentially stable (Toast.tsx), but listing it would still be wrong —
  // an error toast must never re-trigger the request that produced it.
  const loadCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const res = await whatsappListBroadcasts({
        integrationId: selectedAccountId !== ALL_ACCOUNTS ? selectedAccountId : undefined,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        channel: activeChannel !== 'all' ? activeChannel : undefined,
        search: debouncedSearch.trim() || undefined,
        limit: 100,
      });
      if (res.ok && res.broadcasts) {
        setCampaigns(res.broadcasts.map((d: BroadcastDoc) => mapBroadcast(d)));
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLoadingCampaigns(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, statusFilter, activeChannel, debouncedSearch]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  useEffect(() => {
    void loadCampaigns();
  }, [loadCampaigns]);

  // ── Campaign Action Handlers ───────────────────────────────────────────────

  async function handlePause(id: string) {
    setBusyCampaignId(id);
    try {
      const res = await whatsappPauseBroadcast({ broadcastId: id });
      if (res.ok) {
        toast.success(t('bcast.pausedSuccess') || 'Campaign paused');
        await loadCampaigns();
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyCampaignId(null);
    }
  }

  async function handleResume(id: string) {
    setBusyCampaignId(id);
    try {
      const res = await whatsappResumeBroadcast({ broadcastId: id });
      if (res.ok) {
        toast.success(t('bcast.resumedSuccess') || 'Campaign resumed');
        await loadCampaigns();
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyCampaignId(null);
    }
  }

  async function handleCancel(id: string) {
    if (!(await confirm({
      title: t('bcast.cancelCampaignTitle'),
      body: t('bcast.cancelConfirm'),
      confirmLabel: t('bcast.actions.cancel'),
      cancelLabel: t('common.keep'),
      danger: true,
    }))) {
      return;
    }
    setBusyCampaignId(id);
    try {
      const res = await whatsappCancelBroadcast({ broadcastId: id });
      if (res.ok) {
        toast.success(t('bcast.cancelledSuccess') || 'Campaign cancelled');
        await loadCampaigns();
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyCampaignId(null);
    }
  }

  async function handleExportCsv(id: string) {
    setBusyCampaignId(id);
    try {
      const res = await whatsappExportBroadcastReport({ id });
      if (res.ok && res.downloadUrl) {
        window.open(res.downloadUrl, '_blank');
        toast.success(t('bcast.exportSuccess') || `Exported ${res.totalRows} recipient rows`);
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyCampaignId(null);
    }
  }

  async function handleDelete(id: string) {
    if (!(await confirm({ title: t('common.areYouSure'), body: t('bcast.deleteConfirm'), danger: true }))) {
      return;
    }
    setBusyCampaignId(id);
    try {
      const res = await whatsappDeleteBroadcast({ broadcastId: id });
      if (res.ok) {
        toast.success(t('bcast.deletedSuccess') || 'Campaign deleted');
        setCampaigns((prev) => prev.filter((c) => String(c.id) !== id));
        await loadStats();
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusyCampaignId(null);
    }
  }

  // Channel Tabs config
  const channelTabs = [
    {
      id: 'all',
      label: t('bcast.channel.all') || 'All Channels',
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: <SmartphoneIcon size={14} />,
    },
    {
      id: 'sms',
      label: 'SMS',
      icon: <span>💬</span>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title={t('bcast.title') || 'Broadcasting'}
        subtitle={t('bcast.subtitle') || 'Create, schedule, and track bulk WhatsApp campaigns with granular audience targeting.'}
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void loadStats();
                void loadCampaigns();
              }}
            >
              <RefreshIcon size={14} className={loadingCampaigns ? 'animate-spin' : ''} />
            </Button>
            <Button
              variant="primary"
              onClick={() => setCreateDrawerOpen(true)}
            >
              <PlusIcon size={16} className="me-1.5" />
              {t('bcast.create') || 'Create Campaign'}
            </Button>
          </div>
        }
      />

      {/* PillTabs for Channel Selection */}
      <div className="flex items-center justify-between">
        <PillTabs
          tabs={channelTabs}
          activeId={activeChannel}
          onChange={(id) => setActiveChannel(id as any)}
        />
      </div>

      {/* 5 High-Level 30-Day Metric Cards */}
      <BroadcastStatCards stats={stats} loading={loadingStats} />

      {/* FilterBar */}
      <FilterBar
        search={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: t('bcast.searchPlaceholder') || 'Search campaigns by name…',
        }}
      >
        <select
          value={selectedAccountId}
          onChange={(e) => setSelectedAccountId(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white py-1.5 px-3 text-[13px] text-ink focus:border-brand focus:outline-none"
        >
          <option value={ALL_ACCOUNTS}>{t('common.allAccounts') || 'All WhatsApp Numbers'}</option>
          {accounts.numbers.map((n) => (
            <option key={n.id} value={n.id}>
              {numberLabel(n)}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white py-1.5 px-3 text-[13px] text-ink focus:border-brand focus:outline-none"
        >
          <option value="ALL">{t('common.allStatuses') || 'All Statuses'}</option>
          <option value="completed">{t('status.completed') || 'Completed'}</option>
          <option value="sending">{t('status.sending') || 'Sending'}</option>
          <option value="scheduled">{t('status.scheduled') || 'Scheduled'}</option>
          <option value="paused">{t('status.paused') || 'Paused'}</option>
          <option value="draft">{t('status.draft') || 'Draft'}</option>
          <option value="cancelled">{t('status.cancelled') || 'Cancelled'}</option>
          <option value="failed">{t('status.failed') || 'Failed'}</option>
        </select>
      </FilterBar>

      {/* Campaigns Desktop Table */}
      <div className="hidden rounded-card border border-line bg-white shadow-card md:block overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-start text-xs">
            <thead>
              <tr className="border-b border-line bg-surface/50 text-[11px] font-bold uppercase tracking-wider text-ink-3">
                <th className="px-4 py-3 text-start">{t('bcast.col.campaign') || 'Campaign Name'}</th>
                <th className="px-3 py-3 text-start">{t('bcast.col.status') || 'Status'}</th>
                <th className="px-3 py-3 text-start">{t('bcast.col.actions') || 'Actions'}</th>
                <th className="px-3 py-3 text-start">{t('dash.col.processed') || 'Processed'}</th>
                <th className="px-3 py-3 text-start">{t('dash.col.delivered') || 'Delivered'}</th>
                <th className="px-3 py-3 text-start">{t('dash.col.opened') || 'Opened'}</th>
                <th className="px-3 py-3 text-start">{t('dash.col.unreached') || 'Unreached'}</th>
                <th className="px-4 py-3 text-start">{t('dash.col.scheduled') || 'Scheduled At'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {loadingCampaigns ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton.TableRow key={i} cols={8} />
                ))
              ) : campaigns.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8">
                    <EmptyState
                      icon={<BroadcastingIcon size={28} />}
                      title={t('bcast.noCampaigns') || 'No campaigns found'}
                      description={t('bcast.emptyDesc') || 'Create your first broadcast campaign to reach your subscribers.'}
                      action={
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setCreateDrawerOpen(true)}
                          leadingIcon={<PlusIcon size={14} />}
                        >
                          {t('bcast.newCampaign') || 'New Campaign'}
                        </Button>
                      }
                    />
                  </td>
                </tr>
              ) : (
                campaigns.map((c) => {
                  const allowed = statusToAllowedActions(c.status);
                  const isBusy = busyCampaignId === String(c.id);

                  return (
                    <tr key={c.id} className="hover:bg-surface/50 transition-colors">
                      {/* Campaign Name */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5">
                            {c.channel === 'sms' ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                                SMS
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
                                WA
                              </span>
                            )}
                            <span className="font-bold text-ink text-sm">{c.name}</span>
                          </div>
                          <span className="text-[11px] text-ink-3">
                            {t(`bcast.type.${c.type}.tag`) || c.type}
                          </span>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <Badge
                          variant={STATUS_BADGE[c.status]?.variant || 'slate'}
                          dot={STATUS_BADGE[c.status]?.dot}
                        >
                          {t(`status.${c.status}`) || c.status}
                        </Badge>
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          {/* View Detail Drawer */}
                          <button
                            type="button"
                            title={t('bcast.actions.view') || 'View campaign details'}
                            onClick={() => setSelectedDetailId(String(c.id))}
                            className="rounded-lg p-1.5 text-ink-3 hover:bg-slate-100 hover:text-ink transition-colors"
                          >
                            <EyeIcon size={16} />
                          </button>

                          {/* Pause / Resume */}
                          {allowed.canPause && (
                            <button
                              type="button"
                              title={t('bcast.actions.pause') || 'Pause campaign'}
                              disabled={isBusy}
                              onClick={() => handlePause(String(c.id))}
                              className="rounded-lg p-1.5 text-amber-600 hover:bg-amber-50 transition-colors"
                            >
                              <PauseIcon size={16} />
                            </button>
                          )}
                          {allowed.canResume && (
                            <button
                              type="button"
                              title={t('bcast.actions.resume') || 'Resume campaign'}
                              disabled={isBusy}
                              onClick={() => handleResume(String(c.id))}
                              className="rounded-lg p-1.5 text-emerald-600 hover:bg-emerald-50 transition-colors"
                            >
                              <PlayIcon size={16} />
                            </button>
                          )}

                          {/* Cancel */}
                          {allowed.canCancel && (
                            <button
                              type="button"
                              title={t('bcast.actions.cancel') || 'Cancel campaign'}
                              disabled={isBusy}
                              onClick={() => handleCancel(String(c.id))}
                              className="rounded-lg p-1.5 text-rose-600 hover:bg-rose-50 transition-colors"
                            >
                              <StopCircleIcon size={16} />
                            </button>
                          )}

                          {/* Export CSV */}
                          {allowed.canExport && (
                            <button
                              type="button"
                              title={t('bcast.actions.exportCsv') || 'Export report CSV'}
                              disabled={isBusy}
                              onClick={() => handleExportCsv(String(c.id))}
                              className="rounded-lg p-1.5 text-ink-3 hover:bg-slate-100 hover:text-ink transition-colors"
                            >
                              <DownloadIcon size={16} />
                            </button>
                          )}

                          {/* Delete */}
                          {allowed.canDelete && (
                            <button
                              type="button"
                              title={t('bcast.actions.delete') || 'Delete campaign'}
                              disabled={isBusy}
                              onClick={() => handleDelete(String(c.id))}
                              className="rounded-lg p-1.5 text-rose-600 hover:bg-rose-50 transition-colors"
                            >
                              <TrashIcon size={16} />
                            </button>
                          )}
                        </div>
                      </td>

                      {/* 4 Metric Columns */}
                      {METRIC_COLS.map((col) => (
                        <td key={col.field} className="px-3 py-3 min-w-[110px]">
                          <MetricBar
                            icon={col.icon}
                            label={`${c[col.field].pct}%`}
                            pct={c[col.field].pct}
                            sub={c[col.field].sub}
                            bar={col.bar}
                          />
                        </td>
                      ))}

                      {/* Scheduled At */}
                      <td className="px-4 py-3 whitespace-nowrap text-ink-2">
                        {c.scheduledAt}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Cards Layout (< 768px) */}
      <div className="space-y-3 md:hidden">
        {loadingCampaigns ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton.Card key={i} className="p-4" />
          ))
        ) : campaigns.length === 0 ? (
          <EmptyState
            icon={<BroadcastingIcon size={28} />}
            title={t('bcast.noCampaigns') || 'No campaigns found'}
            description={t('bcast.emptyDesc') || 'Create your first broadcast campaign to reach your subscribers.'}
            action={
              <Button
                variant="primary"
                size="sm"
                onClick={() => setCreateDrawerOpen(true)}
                leadingIcon={<PlusIcon size={14} />}
              >
                {t('bcast.newCampaign') || 'New Campaign'}
              </Button>
            }
          />
        ) : (
          campaigns.map((c) => {
            const allowed = statusToAllowedActions(c.status);
            const isBusy = busyCampaignId === String(c.id);

            return (
              <div
                key={c.id}
                className="rounded-card border border-line bg-white p-4 shadow-card space-y-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h4 className="font-bold text-ink text-sm">{c.name}</h4>
                    <span className="text-[11px] text-ink-3">
                      {t(`bcast.type.${c.type}.tag`) || c.type}
                    </span>
                  </div>
                  <Badge
                    variant={STATUS_BADGE[c.status]?.variant || 'slate'}
                    dot={STATUS_BADGE[c.status]?.dot}
                  >
                    {t(`status.${c.status}`) || c.status}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-line">
                  {METRIC_COLS.map((col) => (
                    <MetricBar
                      key={col.field}
                      icon={col.icon}
                      label={`${c[col.field].pct}%`}
                      pct={c[col.field].pct}
                      sub={c[col.field].sub}
                      bar={col.bar}
                    />
                  ))}
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-line text-xs">
                  <span className="text-ink-3">{c.scheduledAt}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setSelectedDetailId(String(c.id))}
                      className="rounded-lg p-1 text-ink-2 hover:bg-slate-100"
                    >
                      <EyeIcon size={16} />
                    </button>
                    {allowed.canPause && (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => handlePause(String(c.id))}
                        className="rounded-lg p-1 text-amber-600 hover:bg-amber-50 disabled:opacity-50"
                      >
                        <PauseIcon size={16} />
                      </button>
                    )}
                    {allowed.canResume && (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => handleResume(String(c.id))}
                        className="rounded-lg p-1 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                      >
                        <PlayIcon size={16} />
                      </button>
                    )}
                    {allowed.canCancel && (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => handleCancel(String(c.id))}
                        className="rounded-lg p-1 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                      >
                        <StopCircleIcon size={16} />
                      </button>
                    )}
                    {allowed.canExport && (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => handleExportCsv(String(c.id))}
                        className="rounded-lg p-1 text-ink-2 hover:bg-slate-100 disabled:opacity-50"
                      >
                        <DownloadIcon size={16} />
                      </button>
                    )}
                    {allowed.canDelete && (
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => handleDelete(String(c.id))}
                        className="rounded-lg p-1 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                      >
                        <TrashIcon size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create Campaign Drawer */}
      <CreateCampaignDrawer
        open={createDrawerOpen}
        initialChannel={activeChannel === 'sms' ? 'sms' : 'whatsapp'}
        onClose={() => setCreateDrawerOpen(false)}
        onSuccess={() => {
          void loadStats();
          void loadCampaigns();
        }}
      />

      {/* Broadcast Detail Drawer */}
      <BroadcastDetailDrawer
        open={Boolean(selectedDetailId)}
        broadcastId={selectedDetailId}
        onClose={() => setSelectedDetailId(null)}
      />
    </div>
  );
}
